import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../common/axios";
import useGet from "../../hooks/useGet";
import { getErrors, toastDefault } from "../../utils/handler.utils";
import Loader from "../Loader/Loader";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEK_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
  gap: "4px",
};

const getStartOfDay = (value = new Date()) => {
  const start = new Date(value);
  start.setHours(0, 0, 0, 0);
  return start;
};

const formatDateOnly = (value) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateOnly = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const parsedDate = new Date(year, month - 1, day);
  if (
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getDate() !== day
  ) {
    return null;
  }
  return getStartOfDay(parsedDate);
};

const addMonthsClamped = (value, monthsToAdd) => {
  const currentYear = value.getFullYear();
  const currentMonth = value.getMonth();
  const currentDate = value.getDate();
  const targetMonthIndex = currentMonth + monthsToAdd;
  const targetYear =
    currentYear + Math.floor(targetMonthIndex / 12);
  const targetMonth =
    ((targetMonthIndex % 12) + 12) % 12;
  const targetMonthLastDate = new Date(targetYear, targetMonth + 1, 0).getDate();
  return new Date(
    targetYear,
    targetMonth,
    Math.min(currentDate, targetMonthLastDate)
  );
};

const getRangeMonths = (rangeStart, rangeEnd) => {
  const months = [];
  const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
  const lastMonth = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), 1);

  while (cursor <= lastMonth) {
    months.push({
      year: cursor.getFullYear(),
      month: cursor.getMonth(),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
};

const getMonthCells = (year, month) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOffset = new Date(year, month, 1).getDay();
  const cells = [];

  for (let i = 0; i < firstDayOffset; i += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
};

const normalizeAvailableDateResponse = (data, rangeStart, rangeEnd) => {
  if (!Array.isArray(data)) return [];
  const uniqueDates = new Set();

  data.forEach((item) => {
    const rawDate = typeof item === "string" ? item : item?.available_date;
    if (!rawDate) return;
    const parsedDate = parseDateOnly(String(rawDate));
    if (!parsedDate) return;
    if (parsedDate < rangeStart || parsedDate > rangeEnd) return;
    uniqueDates.add(formatDateOnly(parsedDate));
  });

  return [...uniqueDates].sort((a, b) => a.localeCompare(b));
};

function SpinAvailableDate() {
  const today = useMemo(() => getStartOfDay(new Date()), []);
  const rangeEnd = useMemo(() => addMonthsClamped(today, 2), [today]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [refreshToggle, setRefreshToggle] = useState(false);

  const [availableDateData, loadingData, loadingError] = useGet(
    "admin/spin-available-dates",
    "",
    refreshToggle
  );

  useEffect(() => {
    const normalizedDates = normalizeAvailableDateResponse(
      availableDateData,
      today,
      rangeEnd
    );
    setSelectedDates(normalizedDates);
  }, [availableDateData, today, rangeEnd]);

  const monthList = useMemo(() => getRangeMonths(today, rangeEnd), [today, rangeEnd]);
  const selectedDateSet = useMemo(() => new Set(selectedDates), [selectedDates]);
  const todayKey = useMemo(() => formatDateOnly(today), [today]);

  const rangeStartText = useMemo(
    () =>
      today.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    [today]
  );
  const rangeEndText = useMemo(
    () =>
      rangeEnd.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    [rangeEnd]
  );

  const toggleDate = (dateKey) => {
    setSelectedDates((prev) => {
      if (prev.includes(dateKey)) {
        return prev.filter((item) => item !== dateKey);
      }
      return [...prev, dateKey].sort((a, b) => a.localeCompare(b));
    });
  };

  const saveAvailableDatesHandler = () => {
    if (selectedDates.length === 0) {
      toast.error("Select at least one available date", toastDefault);
      return;
    }

    setSaving(true);
    axiosInstance
      .post("/admin/spin-available-date/create", {
        available_dates: selectedDates,
      })
      .then((res) => {
        const normalizedDates = normalizeAvailableDateResponse(
          res?.data?.data,
          today,
          rangeEnd
        );
        setSelectedDates(normalizedDates);
        setRefreshToggle((prev) => !prev);
        toast.success("Spin available dates updated successfully", toastDefault);
      })
      .catch((err) => {
        toast.error(getErrors(err, false, true), toastDefault);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const selectAllHandler = () => {
    const allKeys = [];
    monthList.forEach(({ year, month }) => {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(year, month, day);
        if (date >= today && date <= rangeEnd) {
          allKeys.push(formatDateOnly(date));
        }
      }
    });
    setSelectedDates(allKeys);
  };

  return (
    <section className="relative container_admin">
      <div className="bg-white overflow-hidden rounded">
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-bold text-black">
              Spin Available Dates
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Select dates between {rangeStartText} and {rangeEndText}
            </p>
          </div>
          <Link className="cstm_btn_small" to="/spin">
            Back to spin
          </Link>
        </div>

        <div className="py-8 px-4">
          <div className="w-full xl:w-[92%] mx-auto relative border border-gray-200 rounded px-4 py-5">
            {(loadingData || saving) && <Loader absolute />}

            {!loadingData && loadingError && (
              <div className="text-center py-6">
                <ul>{getErrors(loadingError, false, true)}</ul>
              </div>
            )}

            {!loadingData && !loadingError && (
              <>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm text-gray-600">
                    Selected dates:{" "}
                    <span className="font-semibold">{selectedDates.length}</span>
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="cstm_btn_small !bg-slate-600 hover:!bg-slate-700"
                      onClick={selectAllHandler}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="cstm_btn_small !bg-gray-600 hover:!bg-gray-700"
                      onClick={() => setSelectedDates([])}
                    >
                      Clear all
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-5">
                  {monthList.map(({ year, month }) => {
                    const monthName = new Date(year, month, 1).toLocaleDateString(
                      "en-US",
                      {
                        month: "long",
                        year: "numeric",
                      }
                    );
                    const monthCells = getMonthCells(year, month);

                    return (
                      <div
                        key={`${year}-${month}`}
                        className="border border-gray-200 rounded p-3"
                      >
                        <h4 className="font-semibold text-sm text-black mb-3">
                          {monthName}
                        </h4>

                        <div style={WEEK_GRID_STYLE}>
                          {DAY_NAMES.map((dayName) => (
                            <div
                              key={`${monthName}-${dayName}`}
                              className="text-[11px] font-medium text-center text-gray-500 pb-1"
                            >
                              {dayName}
                            </div>
                          ))}

                          {monthCells.map((date, index) => {
                            if (!date) {
                              return (
                                <div
                                  key={`${year}-${month}-empty-${index}`}
                                  className="h-9"
                                />
                              );
                            }

                            const dateKey = formatDateOnly(date);
                            const isInRange = date >= today && date <= rangeEnd;
                            const isSelected = selectedDateSet.has(dateKey);
                            const isToday = dateKey === todayKey;

                            let buttonClass =
                              "w-full h-10 rounded text-sm border transition-all flex items-center justify-center";
                            let buttonStyle = undefined;
                            if (!isInRange) {
                              buttonClass +=
                                " cursor-not-allowed bg-gray-50 text-gray-300 border-gray-100";
                            } else if (isSelected) {
                              buttonClass += " text-white";
                              buttonStyle = {
                                backgroundColor: "#0284c7",
                                borderColor: "#0284c7",
                              };
                            } else {
                              buttonClass +=
                                " bg-white text-gray-700 border-gray-200 hover:border-green-500 hover:text-green-700";
                            }

                            if (isToday && isInRange && !isSelected) {
                              buttonClass += " font-semibold";
                            }

                            return (
                              <button
                                key={dateKey}
                                type="button"
                                disabled={!isInRange || saving}
                                onClick={() => toggleDate(dateKey)}
                                className={buttonClass}
                                style={buttonStyle}
                                title={date.toDateString()}
                              >
                                {date.getDate()}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6">
                  <button
                    type="button"
                    className="cstm_btn w-full block"
                    onClick={saveAvailableDatesHandler}
                    disabled={saving}
                  >
                    Save available dates
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default SpinAvailableDate;
